import { useSyncExternalStore } from 'react';

/**
 * Days left before the licence locks, as reported by the X-License-Warning
 * header. Negative means the due date has passed and the grace window is
 * running. `null` = nothing to report (no expiry set, or still far away).
 *
 * The header is emitted by both runtimes — the cloud gate
 * (backend/src/core/middlewares/license.middleware.ts) and the local server's
 * licenseGate — so this works with the cloud unreachable, and through the
 * local-server proxy, without a second mechanism.
 */
let daysRemaining: number | null = null;
/** Set once any call comes back 423 — the licence is locked, not just expiring. */
let lockedMessage: string | null = null;
const listeners = new Set<() => void>();

function emit(): void {
    listeners.forEach(l => l());
}

export function setLicenseWarning(raw: string | null | undefined): void {
    const next = raw == null || raw === '' ? null : Number(raw);
    const value = next != null && Number.isFinite(next) ? next : null;

    // This is only ever called for a response that got PAST the licence gate (a
    // success, or a non-423 error) — which by itself proves the licence is not
    // locked. Deliberately not inferred from the header value: that would couple
    // the client to the server's grace window and strand the overlay on screen if
    // LICENSE_GRACE_DAYS were ever raised.
    //
    // Computed BEFORE the no-op check: a restaurant locked on its very first call
    // never got daysRemaining set, so it is still null here, and returning early
    // on `null === null` would leave the overlay up until a manual reload.
    const clearsLock = lockedMessage !== null;
    if (value === daysRemaining && !clearsLock) return;

    daysRemaining = value;
    lockedMessage = null;
    emit();
}

export function setLicenseLocked(message?: string): void {
    const next = message || "Licence expirée — contactez votre administrateur pour renouveler l'accès.";
    if (next === lockedMessage) return;
    lockedMessage = next;
    emit();
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function useLicenseWarning(): number | null {
    return useSyncExternalStore(subscribe, () => daysRemaining, () => null);
}

export function useLicenseLocked(): string | null {
    return useSyncExternalStore(subscribe, () => lockedMessage, () => null);
}
